/**
 * SEOHead.jsx
 * 
 * Dynamic meta tags and JSON-LD structured data for SEO/AEO.
 * Uses react-helmet-async to inject into <head>.
 * 
 * Usage:
 *   <SEOHead 
 *     title="Page Title"
 *     description="Page description"
 *     path="/blog/my-post"
 *     type="article"
 *     article={{ publishedAt: '2026-01-15', updatedAt: '2026-02-01' }}
 *     faq={[{ question: 'Q1?', answer: 'A1' }]}
 *   />
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { getApprovedAuthor } from '../data/authors.js';
import { CANONICAL_URL, SITE_NAME, ORGANIZATION_ID, WEBSITE_ID } from '../data/siteConfig.js';

const DEFAULT_IMAGE = `${CANONICAL_URL}/FullLogo_Transparent.png`;
const DEFAULT_DESCRIPTION = 'Neutral research platform documenting real-world AI tool usage. Compare AI tools based on your context, not rankings.';

/**
 * Generate Organization JSON-LD (included on every page)
 */
function getOrganizationSchema() {
  return {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: SITE_NAME,
    url: CANONICAL_URL,
    logo: `${CANONICAL_URL}/FullLogo_Transparent.png`,
    description: 'Neutral research platform documenting real-world AI tool usage.',
    sameAs: [],
  };
}

function getWebsiteSchema() {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: SITE_NAME,
    url: CANONICAL_URL,
    publisher: { '@id': ORGANIZATION_ID },
  };
}

function getAuthorSchema(authorKey) {
  const author = getApprovedAuthor(authorKey);
  if (!author) return null;

  return {
    '@type': 'Person',
    '@id': `${CANONICAL_URL}#/authors/${author.key}`,
    name: author.name,
    jobTitle: author.title,
    description: author.bio,
    worksFor: { '@id': ORGANIZATION_ID },
  };
}

function resolveImageUrl(image) {
  if (!image) return DEFAULT_IMAGE;
  if (image.startsWith('http') || image.startsWith('data:')) return image;
  return `${CANONICAL_URL}${image}`;
}

/**
 * Generate BreadcrumbList JSON-LD
 */
function getBreadcrumbSchema(breadcrumbs) {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;
  
  return {
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
}

/**
 * Generate Article JSON-LD (for blog posts)
 */
function getArticleSchema({ title, description, path, publishedAt, updatedAt, image, authorKey }) {
  const authorSchema = getAuthorSchema(authorKey);
  return {
    '@type': 'BlogPosting',
    '@id': `${CANONICAL_URL}${path}#article`,
    headline: title,
    description: description,
    datePublished: publishedAt,
    dateModified: updatedAt || publishedAt,
    author: authorSchema ? { '@id': authorSchema['@id'] } : { '@id': ORGANIZATION_ID },
    publisher: { '@id': ORGANIZATION_ID },
    image: image || DEFAULT_IMAGE,
    mainEntityOfPage: `${CANONICAL_URL}${path}`,
  };
}

/**
 * Generate FAQPage JSON-LD
 */
function getFAQSchema(faq) {
  if (!faq || faq.length === 0) return null;
  
  return {
    '@type': 'FAQPage',
    mainEntity: faq.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

/**
 * Generate SoftwareApplication JSON-LD (for tool pages)
 */
function getSoftwareSchema({ name, category, description, path }) {
  return {
    '@type': 'SoftwareApplication',
    name: name,
    applicationCategory: category,
    description: description,
    url: `${CANONICAL_URL}${path}`,
  };
}

/**
 * Generate WebPage JSON-LD (for generic pages)
 */
function getWebPageSchema({ title, description, path, dateModified }) {
  const schema = {
    '@type': 'WebPage',
    '@id': `${CANONICAL_URL}${path}#webpage`,
    name: title,
    description: description,
    url: `${CANONICAL_URL}${path}`,
    isPartOf: { '@id': WEBSITE_ID },
  };

  if (dateModified) {
    schema.dateModified = dateModified;
  }

  return schema;
}

export default function SEOHead({
  title,
  description,
  path = '/',
  dateModified,
  type = 'website', // 'website' | 'article' | 'tool'
  image,
  article, // { publishedAt, updatedAt }
  tool, // { name, category }
  faq, // [{ question, answer }]
  breadcrumbs, // [{ name, url }]
  noindex = false,
  extraGraph = [],
  structuredData = null,
}) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : SITE_NAME;
  const metaDescription = description || DEFAULT_DESCRIPTION;
  const canonicalUrl = `${CANONICAL_URL}${path}`;
  const resolvedImage = resolveImageUrl(image);
  const socialImage = resolvedImage.startsWith('data:') ? DEFAULT_IMAGE : resolvedImage;
  
  // Build JSON-LD graph
  const jsonLdGraph = [getOrganizationSchema(), getWebsiteSchema()];
  
  // Add breadcrumbs if provided
  if (breadcrumbs) {
    const breadcrumbSchema = getBreadcrumbSchema(breadcrumbs);
    if (breadcrumbSchema) jsonLdGraph.push(breadcrumbSchema);
  }
  
  // Add type-specific schema
  if (type === 'article' && article) {
    jsonLdGraph.push(getArticleSchema({
      title,
      description: metaDescription,
      path,
      publishedAt: article.publishedAt,
      updatedAt: article.updatedAt,
      image: socialImage,
      authorKey: article.authorKey,
    }));
  } else if (type === 'tool' && tool) {
    jsonLdGraph.push(getSoftwareSchema({
      name: tool.name,
      category: tool.category,
      description: metaDescription,
      path,
    }));
  } else {
    jsonLdGraph.push(getWebPageSchema({
      title,
      description: metaDescription,
      path,
      dateModified,
    }));
  }
  
  // Add FAQ if provided
  if (faq && faq.length > 0) {
    const faqSchema = getFAQSchema(faq);
    if (faqSchema) jsonLdGraph.push(faqSchema);
  }

  if (type === 'article' && article?.authorKey) {
    const authorSchema = getAuthorSchema(article.authorKey);
    if (authorSchema) jsonLdGraph.push(authorSchema);
  }

  if (extraGraph.length > 0) {
    jsonLdGraph.push(...extraGraph.filter(Boolean));
  }
  
  const jsonLd = structuredData || {
    '@context': 'https://schema.org',
    '@graph': jsonLdGraph,
  };
  
  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      
      {/* Open Graph */}
      <meta property="og:type" content={type === 'article' ? 'article' : 'website'} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={socialImage} />
      <meta property="og:locale" content="en_US" />
      
      {/* Article-specific Open Graph */}
      {type === 'article' && article?.publishedAt && (
        <meta property="article:published_time" content={article.publishedAt} />
      )}
      {type === 'article' && article?.updatedAt && (
        <meta property="article:modified_time" content={article.updatedAt} />
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={socialImage} />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
    </Helmet>
  );
}

/**
 * Helper: Extract FAQ from markdown content
 * Looks for ## FAQ or ### FAQ section and parses Q&A in two formats:
 *   Format A (preferred): ### Question heading followed by answer paragraph
 *   Format B (legacy):    **Q: Question**\nA: Answer
 */
export function extractFAQFromContent(content) {
  if (!content) return [];

  const faqHeaderPattern = /^## (?:FAQ|What are (?:the )?(?:most )?common (?:questions|FAQs).*\?)/im;
  const faqHeaderMatch = content.match(faqHeaderPattern);
  if (!faqHeaderMatch) return [];

  const faqStart = content.indexOf(faqHeaderMatch[0]) + faqHeaderMatch[0].length;
  const faqRemainder = content.slice(faqStart);
  const nextH2Index = faqRemainder.search(/\n## [^#]/);
  const faqSection = nextH2Index >= 0 ? faqRemainder.slice(0, nextH2Index) : faqRemainder;
  const faq = [];
  
  // Format A: ### Question headings
  const headingRegex = /### (.+?)\n\n([\s\S]*?)(?=\n### |$)/g;
  let match;
  while ((match = headingRegex.exec(faqSection)) !== null) {
    faq.push({
      question: match[1].trim(),
      answer: match[2].trim(),
    });
  }
  
  if (faq.length > 0) return faq;
  
  // Format B: **Q: Question**\nA: Answer (bold Q/A pairs)
  const boldRegex = /\*\*Q:\s*(.+?)\*\*\s*\n\s*A:\s*([\s\S]*?)(?=\n\s*\*\*Q:|$)/g;
  while ((match = boldRegex.exec(faqSection)) !== null) {
    faq.push({
      question: match[1].trim(),
      answer: match[2].trim(),
    });
  }

  if (faq.length > 0) return faq;

  // Format C: * **Question** Answer (bullet-style)
  const bulletRegex = /\*\s*\*\*(.+?)\*\*\s*([\s\S]*?)(?=\n\*\s*\*\*|$)/g;
  while ((match = bulletRegex.exec(faqSection)) !== null) {
    faq.push({
      question: match[1].trim(),
      answer: match[2].trim(),
    });
  }
  
  return faq;
}
